import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAutomationClient } from '../providers/AutomationProvider'
import type { TestCasesListParams, CreateTestCaseParams, UpdateTestCaseParams } from '../types'
import { tracesKeys } from './useTraces'

/**
 * Query key factory for test cases-related queries
 */
export const testCasesKeys = {
  all: ['testCases'] as const,
  lists: () => [...testCasesKeys.all, 'list'] as const,
  list: (params?: TestCasesListParams) => [...testCasesKeys.lists(), params] as const,
  details: () => [...testCasesKeys.all, 'detail'] as const,
  detail: (id: string) => [...testCasesKeys.details(), id] as const,
}

/**
 * Hook to fetch a paginated list of test cases
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useTestCases({ status: 'ACTIVE' })
 * console.log(data?.testCases)
 * ```
 */
export function useTestCases(params?: TestCasesListParams) {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: testCasesKeys.list(params),
    queryFn: () => client.testCases.list(params),
  })
}

/**
 * Hook to fetch a single test case with its run history
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useTestCase('test-123')
 * console.log(data?.runs)
 * ```
 */
export function useTestCase(id: string, options?: { enabled?: boolean }) {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: testCasesKeys.detail(id),
    queryFn: () => client.testCases.get(id),
    enabled: options?.enabled !== false && !!id,
  })
}

/**
 * Hook to create a new test case
 *
 * @example
 * ```tsx
 * const { mutate: createTestCase } = useCreateTestCase()
 * createTestCase({
 *   automationId: 'auto-123',
 *   name: 'Login Test',
 *   triggerType: 'manual',
 *   triggerPayload: { username: 'test' }
 * })
 * ```
 */
export function useCreateTestCase() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateTestCaseParams) => client.testCases.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testCasesKeys.lists() })
    },
  })
}

/**
 * Hook to update an existing test case
 *
 * @example
 * ```tsx
 * const { mutate: updateTestCase } = useUpdateTestCase()
 * updateTestCase({ id: 'test-123', data: { name: 'Updated Name' } })
 * ```
 */
export function useUpdateTestCase() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTestCaseParams }) =>
      client.testCases.update(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: testCasesKeys.lists() })
      queryClient.invalidateQueries({ queryKey: testCasesKeys.detail(id) })
    },
  })
}

/**
 * Hook to delete a test case
 *
 * @example
 * ```tsx
 * const { mutate: deleteTestCase } = useDeleteTestCase()
 * deleteTestCase('test-123')
 * ```
 */
export function useDeleteTestCase() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => client.testCases.delete(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: testCasesKeys.lists() })
      queryClient.removeQueries({ queryKey: testCasesKeys.detail(id) })
    },
  })
}

/**
 * Hook to run a test case
 *
 * @example
 * ```tsx
 * const { mutate: runTest, isPending } = useRunTestCase()
 * runTest('test-123', {
 *   onSuccess: (result) => console.log(result.testRun.status)
 * })
 * ```
 */
export function useRunTestCase() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => client.testCases.run(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: testCasesKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: testCasesKeys.lists() })
      queryClient.invalidateQueries({ queryKey: tracesKeys.lists() })
    },
  })
}
